import type { WorkoutExerciseLog } from '../../api/workoutApi'

export interface WorkoutGroup {
  /** First log sort order in group — used for ordering groups. */
  orderKey: number
  groupSortOrder: number | null
  note: string | null
  exercises: WorkoutExerciseLog[]
}

function soloKey(log: WorkoutExerciseLog): string {
  return `solo:${log.id}`
}

/** Group flat exercise logs by groupSortOrder; null groupSortOrder → one log per group. */
export function groupWorkoutExercises(exercises: WorkoutExerciseLog[]): WorkoutGroup[] {
  if (!exercises.length) return []

  const sorted = [...exercises].sort((a, b) => a.sortOrder - b.sortOrder)
  const groups: WorkoutGroup[] = []
  const groupMap = new Map<number, WorkoutGroup>()
  const soloSeen = new Set<string>()

  for (const ex of sorted) {
    if (ex.groupSortOrder == null) {
      const key = soloKey(ex)
      if (soloSeen.has(key)) continue
      soloSeen.add(key)
      groups.push({
        orderKey: ex.sortOrder,
        groupSortOrder: null,
        note: ex.groupNote,
        exercises: [ex],
      })
      continue
    }

    const gKey = ex.groupSortOrder
    let group = groupMap.get(gKey)
    if (!group) {
      group = {
        orderKey: ex.sortOrder,
        groupSortOrder: gKey,
        note: ex.groupNote,
        exercises: [],
      }
      groupMap.set(gKey, group)
      groups.push(group)
    }
    group.exercises.push(ex)
    if (!group.note && ex.groupNote?.trim()) {
      group.note = ex.groupNote
    }
  }

  for (const group of groups) {
    group.exercises.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  groups.sort((a, b) => a.orderKey - b.orderKey)
  return groups
}

export function groupIndexForLogId(groups: WorkoutGroup[], logId: number | null | undefined): number {
  if (logId == null || !groups.length) return 0
  const idx = groups.findIndex(g => g.exercises.some(ex => ex.id === logId))
  return idx >= 0 ? idx : 0
}

export function groupTitle(group: WorkoutGroup): string {
  if (group.exercises.length === 1) return group.exercises[0].exerciseName
  return group.exercises.map(ex => ex.exerciseName).join(' · ')
}
